import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Select } from '@arco-design/web-react';

import { useSyncOptionMetaField } from '../../platform/option-meta';
import { DEFAULT_RECLAIM_POLICY } from './constants';
import { useReclaimPolicyOptions } from './hooks/useReclaimPolicyOptions';
import type { ReclaimPolicyFormFieldProps } from './types';
import { attachReclaimPolicyDescriptions, buildReclaimPolicySelectExtra, getStaticReclaimPolicyOptions } from './utils';

/** 回收策略表单项：优先拉取 volume 配置；失败或空列表时回退静态选项 */
export function ReclaimPolicyFormField({
  form,
  field,
  enabled = true,
  enableStaticFallback = true,
  getPopupContainer,
  formItemClassName,
  formItemStyle,
  requiredMessage,
  placeholder,
  disabled,
  lockedToDelete = false,
}: ReclaimPolicyFormFieldProps) {
  const { t } = useTranslation();
  const staticOptions = useMemo(() => getStaticReclaimPolicyOptions(), []);
  const fetchEnabled = enabled && !lockedToDelete;
  const { options: apiOptions, isLoading, error, isReady } = useReclaimPolicyOptions(fetchEnabled);

  const useStaticFallback = enableStaticFallback && fetchEnabled && isReady && (Boolean(error) || apiOptions.length === 0);
  const reclaimPolicyOptions = useMemo(
    () => attachReclaimPolicyDescriptions(useStaticFallback ? staticOptions : apiOptions, t),
    [apiOptions, staticOptions, t, useStaticFallback],
  );

  const selectOptions = useMemo(
    () =>
      reclaimPolicyOptions.map((opt) => ({
        value: opt.value,
        extra: buildReclaimPolicySelectExtra(opt),
        label: opt.description ? (
          <>
            <span style={{ display: 'inline-block', marginInlineEnd: '1.5rem' }}>{opt.label}</span>
            <span style={{ color: 'var(--color-text-3)' }}>{opt.description}</span>
          </>
        ) : (
          opt.label
        ),
      })),
    [reclaimPolicyOptions],
  );

  useSyncOptionMetaField(form, { field, listItems: reclaimPolicyOptions, enabled: enabled && !lockedToDelete });

  /** SKU / 下单：隐藏 UI，仍强制写入 Delete */
  useEffect(() => {
    if (!lockedToDelete || !enabled) return;
    form.setFieldValue(field, DEFAULT_RECLAIM_POLICY);
  }, [enabled, field, form, lockedToDelete]);

  if (lockedToDelete) {
    return (
      <Form.Item field={field} initialValue={DEFAULT_RECLAIM_POLICY} hidden style={{ display: 'none', margin: 0 }}>
        <Input />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      field={field}
      label={t('common:reclaim.policy')}
      rules={[{ required: true, message: requiredMessage ?? t('common:validate.not-empty') }]}
      className={formItemClassName}
      style={formItemStyle}
    >
      <Select
        options={selectOptions}
        showSearch
        disabled={disabled}
        placeholder={placeholder ?? t('components:please.select.reclaim.policy')}
        loading={fetchEnabled && isLoading}
        getPopupContainer={getPopupContainer}
      />
    </Form.Item>
  );
}
